import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", role: "employee" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => { const r = await api.get("/users"); setUsers(r.data); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.email || !form.password || !form.name) { toast.error("All fields required"); return; }
    try { await api.post("/auth/register", form); toast.success("User created"); setOpen(false); setForm(EMPTY); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => { if (!window.confirm("Delete user?")) return; await api.delete(`/users/${id}`); load(); };

  return (
    <div data-testid="users-page">
      <PageHeader
        overline="Access"
        title="Team & Users"
        description="Invite employees and accountants. Permissions enforced server-side."
        action={<Button data-testid="add-user" onClick={() => { setForm(EMPTY); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"><Plus size={16} className="mr-2" />Add User</Button>}
      />

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Name", "Email", "Role", "Created", ""].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border hover:bg-secondary/40">
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 text-xs capitalize font-semibold">{u.role}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3 text-right">{u.id !== me?.id && <button onClick={() => remove(u.id)} className="p-1.5 rounded-md hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Add User</DialogTitle>
            <DialogDescription>Create login for an employee or accountant.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="u-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Email</Label><Input data-testid="u-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Temporary Password</Label><Input data-testid="u-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-lg" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="u-role" className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="accountant">Accountant</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button data-testid="u-save" onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Create User</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
